<script lang="ts" setup>
import AuthLayout from '@/layouts/AuthLayout.vue';
import ReportFilter from "@/components/features/reports/ReportFilter.vue";
import BatchStockReportDialog from "@/components/features/reports/BatchStockReportDialog.vue";
import { getBatchStock } from '@/api/reports';
import { ref, type Ref, computed, onMounted } from "vue";
import ProgressSpinner from 'primevue/progressspinner';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import type { BatchStockResponse, BatchStockParams } from '@/types/report-types';
import {BatchStateValue} from '@/types/batch-types';
import type {Product} from "@/types/product-types";
import {getProductList} from "@/api/products";

const toast = useToast();

const selectedDate = ref<Date | null>(null);
const selectedQuality = ref<string | null>('All');
const selectedBatchState = ref<BatchStateValue | null>(null);

const reportData: Ref<BatchStockResponse | null> = ref(null);
const isLoading = ref(false)
const isLoadingQualities = ref(false)
const errorMessage = ref<string | null>(null);
const showReportDialog = ref(false);
const fetchedProducts = ref<Product[]>([])

const qualityOptions = computed(() => {
  const options: { label: string; value: string }[] = [
    { label: 'All Qualities', value: 'All' }
  ];

  if (fetchedProducts.value.length > 0) {
    const sortedProducts = [...fetchedProducts.value].sort((a, b) => {
      const refA = a.unfinishedReference || a.finishedReference || '';
      const refB = b.unfinishedReference || b.finishedReference || '';
      return refA.localeCompare(refB);
    });

    sortedProducts.forEach(product => {
      if (product.id) {
        const displayName = product.unfinishedReference || product.finishedReference || 'Unknown Quality';
        options.push({
          label: displayName,
          value: product.id
        });
      }
    });
  }

  return options;
});

const batchStateOptions = computed(() => [
  { label: 'All States', value: 'All' },
  { label: 'Greig', value: BatchStateValue.GREIG },
  { label: 'Offsite', value: BatchStateValue.OFFSITE },
  { label: 'Finished', value: BatchStateValue.FINISHED },
  { label: 'Available', value: BatchStateValue.AVAILABLE },
  { label: 'Dispatched', value: BatchStateValue.DISPATCHED },
]);

const handleGenerateReport = async () => {
  if (!selectedDate.value) {
    toast.add({ severity: 'warn', summary: 'Validation Error', detail: 'Please select a date.', life: 3000 });
    return;
  }

  isLoading.value = true;
  errorMessage.value = null;
  reportData.value = null;

  try {
    const params: BatchStockParams = {
      date: selectedDate.value.toISOString(),
      qualityId: selectedQuality.value && selectedQuality.value !== 'All' ? selectedQuality.value : undefined,
      batchState: selectedBatchState.value !== null && selectedBatchState.value.toString() !== 'All' ? selectedBatchState.value : undefined,
    };

    const data = await getBatchStock(params);

    if (data && data.rolls) {
      reportData.value = data;
      if (data.rolls.length > 0) {
        showReportDialog.value = true;
      } else {
        toast.add({ severity: 'info', summary: 'No Data', detail: 'No batch stock data found for the selected criteria.', life: 4000 });
        showReportDialog.value = false;
      }
    } else {
      reportData.value = null;
      toast.add({ severity: 'info', summary: 'No Data', detail: 'No batch stock data found for the selected criteria (empty response).', life: 4000 });
      showReportDialog.value = false;
    }

  } catch (error: any) {
    console.error("Error fetching Batch Roll Stock report:", error);
    const apiErrorMessage = error.response?.data?.title || error.response?.data?.message || error.message || "Failed to fetch report data.";
    errorMessage.value = apiErrorMessage;
    toast.add({ severity: 'error', summary: 'API Error', detail: apiErrorMessage, life: 5000 });
    showReportDialog.value = false;
  } finally {
    isLoading.value = false;
  }
};

const handleCloseReportDialog = () => {
  showReportDialog.value = false;
};



onMounted(async () => {
  isLoadingQualities.value = true
  try {
    fetchedProducts.value = await getProductList()
    if (qualityOptions.value.length > 0 && selectedQuality.value === null) {
      selectedQuality.value = qualityOptions.value[0].value
    }
  } catch (error: any) {
    console.error('Error fetching quality options:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load quality options.',
      life: 3000
    })
  } finally {
    isLoadingQualities.value = false
  }
})
</script>

<template>
  <AuthLayout>
    <div class="min-h-screen p-4 md:p-6">
      <ReportFilter
          title="Batch Stock Report"
          dropdownLabel="Quality"
          v-model="selectedQuality"
          :dropdownOptions="qualityOptions"
          :disabled="false"
          placeholder="Select Quality"
          :showCurrencyFilter="false"
          :singleDateMode="true"
          :showBatchStateFilter="true"
          :batchState="selectedBatchState as BatchStateValue"
          :batchStateOptions="batchStateOptions"
          @update:startDate="selectedDate = $event"
          @update:batchState="selectedBatchState = $event as BatchStateValue"
          @generate-report="handleGenerateReport"
      />

      <div v-if="isLoading" class="flex justify-center items-center mt-10">
        <ProgressSpinner strokeWidth="4" style="width: 50px; height: 50px" animationDuration=".5s"/>
        <p class="ml-3 text-lg text-gray-600">Generating Report...</p>
      </div>

      <Message v-if="errorMessage && !showReportDialog" severity="error" :closable="true" class="mt-6">
        {{ errorMessage }}
      </Message>

      <div v-if="!isLoading && !reportData && !errorMessage && !showReportDialog" class="mt-10 text-center text-gray-500">
        <p>Select a date and criteria, then click "Create Report" to view batch stock details.</p>
      </div>

      <BatchStockReportDialog
          :visible="showReportDialog"
          :reportData="reportData"
          :selectedDate="selectedDate"
          :selectedQuality="selectedQuality"
          :selectedBatchState="selectedBatchState"
          :onClose="handleCloseReportDialog"
      />
    </div>
  </AuthLayout></template>
